import React, {useEffect, useState} from "react";
import {
    FeaturedProductDetails,
    FeaturedProductFormValues,
    featuredProductFormValuesFormValidation,
    ProductReference
} from "@/data/entities/product";
import {FormattedMessage, useIntl} from "react-intl";
import {Controller, useFormContext, UseFormSetValue} from "react-hook-form";
import {Button, Icon, InputLabel, TextareaField, TextField, useNotifications} from "peggirkit";
import {defaultFormGridClassname, FormGridFiller} from "@widgets/layout/formLayout/FormLayout";
import {twJoin} from "tailwind-merge";
import {animatePulse} from "@/utils/LoadingUtils";
import {getInputValidationProps} from "@/data/Validation";
import {formatDateTime} from "@/utils/DateUtils";
import {CheckIcon} from "@heroicons/react/16/solid";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {editFeaturedProduct} from "@/routing/Router";
import {useNavigate} from "react-router-dom";
import ProductReferenceCombobox from "@widgets/featuredProducts/editor/card/productSearch/ProductReferenceCombobox";
import {SetFeaturedProductProps} from "@pages/authenticated/admin/products/FeaturedProduct";
import {api} from "@/data/api";

const setValues = (
    featuredProduct: FeaturedProductDetails | null,
    setValue: UseFormSetValue<FeaturedProductFormValues>,
) => {
    if (!featuredProduct) {
        return;
    }

    setValue("description", featuredProduct.description);
    setValue("orderNumber", featuredProduct.orderNumber);
    setValue("productId", featuredProduct.product ? featuredProduct.product.id : null);
};

type Props = SetFeaturedProductProps & {
    initialLoading: boolean,
    restaurantId: number,
};

const FeaturedProductDetailsForm = ({featuredProduct, setFeaturedProduct, initialLoading, restaurantId}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const {notify} = useNotifications();
    const {
        control,
        setValue,
        formState: {errors},
        register,
        watch,
        handleSubmit,
        reset
    } = useFormContext<FeaturedProductFormValues>();
    const {mutate: upsertFeaturedProduct} = useMutation({
        mutationFn: (form: FeaturedProductFormValues) => {
            if (featuredProduct === null) {
                return api.featuredProduct.createFeaturedProduct(restaurantId, form);
            } else {
                return api.featuredProduct.updateFeaturedProduct(restaurantId, featuredProduct.id, form);
            }
        },
        onSuccess: (upsertedFeaturedProduct: FeaturedProductDetails) => {
            if (featuredProduct === null) {
                navigate(editFeaturedProduct(upsertedFeaturedProduct.id as number).absolutePath());
                window.scrollTo({top: 0});
            }

            setFeaturedProduct(upsertedFeaturedProduct);
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
        onSettled: () => setLoading(false),
    });
    const disabledState = initialLoading || loading;
    const handleSubmitHandler: (() => void) = handleSubmit((data: FeaturedProductFormValues) => {
        setLoading(true);
        upsertFeaturedProduct(data);
    });

    // Set values after loading existing employee
    useEffect(() => {
        reset();
        setValues(featuredProduct, setValue);
    }, [featuredProduct, setValue, reset]);

    return (
        <div className={defaultFormGridClassname}>
            {
                featuredProduct !== null &&
                <div
                    className={twJoin(
                        defaultFormGridClassname,
                        "md:col-span-2",
                    )}
                >
                    <TextField
                        type={"number"}
                        label={<InputLabel><FormattedMessage id={"id"}/></InputLabel>}
                        tip={<FormattedMessage id={"internalIdReferencePurposes"}/>}
                        defaultValue={featuredProduct.id}
                        disabled={true}
                    />

                    <TextField
                        type={"text"}
                        label={<InputLabel><FormattedMessage id={"addedOn"}/></InputLabel>}
                        disabled={true}
                        defaultValue={formatDateTime(featuredProduct.creationDate)}
                    />
                </div>
            }

            <div className={"md:col-span-2"}>
                <Controller
                    control={control}
                    name={"productId"}
                    rules={{
                        ...getInputValidationProps(featuredProductFormValuesFormValidation.productId, intl),
                    }}
                    render={({field: {value, onChange}}) =>
                        <ProductReferenceCombobox
                            initialLoading={initialLoading}
                            restaurantId={restaurantId}
                            value={value}
                            onChange={(selected: ProductReference) => onChange(selected.id)}
                            error={errors.productId && errors.productId.message}
                        />
                    }
                />
            </div>

            <TextareaField
                containerClassname={twJoin("md:col-span-2", animatePulse(initialLoading))}
                rows={3}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"description"}/>
                    </InputLabel>
                }
                tip={
                    intl.formatMessage({id: "nOutOfTCharacters"}, {
                        n: watch("description")?.length,
                        t: featuredProductFormValuesFormValidation.description.maxLength
                    }) +
                    " - " +
                    intl.formatMessage({id: "featuredProductDescriptionDescription"})
                }
                {...register("description", {
                    ...getInputValidationProps(featuredProductFormValuesFormValidation.description, intl),
                })}
                disabled={disabledState}
                error={errors.description && errors.description.message}
            />

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"number"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"orderNumber"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "featuredProductOrderNumberTip"})}
                {...register("orderNumber", {
                    ...getInputValidationProps(featuredProductFormValuesFormValidation.orderNumber, intl),
                })}
                disabled={disabledState}
                error={errors.orderNumber && errors.orderNumber.message}
            />
            <FormGridFiller/>

            <div className={"w-full md:col-span-2 flex flex-row items-center justify-end"}>
                <Button
                    props={{type: "submit"}}
                    iconBefore={<Icon icon={CheckIcon}/>}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                handleSubmitHandler();
                            },
                        },
                    }}
                    disabled={disabledState}
                    loading={loading}
                >
                    <FormattedMessage id={"save"}/>
                </Button>
            </div>
        </div>
    );
};

export default FeaturedProductDetailsForm;